import type Controller from '@ember/controller';
import { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismCard,
  PrismTable,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import HotelPricingAssistantTableCellsTdRoom from 'lumen/components/hotel/pricing-assistant/table/cells/td-room';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import TdRollingReferenceRate from 'lumen/components/settings/recommendations/td-rolling-reference-rate';
import type { RecommendationStrategy } from 'lumen/models';
import type { RollingReferenceRatesSettings } from 'lumen/models/recommendation-strategy';
import type { RecommendationsModel } from 'lumen/routes/hotel/settings/recommendations';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { getWeekDayColumns } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsRecommendationsRollingReferenceRatesSignature {
  Args: {
    model: RecommendationsModel;
  };
}

export default class SettingsRecommendationsRollingReferenceRates extends Component<SettingsRecommendationsRollingReferenceRatesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  @controller('hotel.settings.recommendations')
  declare recommendationsController: Controller & {
    strategiesPerType: {
      rollingReferenceRatesStrategy: RecommendationStrategy;
    };
  };

  @tracked isAutoMode = true;
  @tracked isEditMode = false;

  get columns(): CustomEmberTableColumn[] {
    const roomColumn: CustomEmberTableColumn = {
      name: '',
      valuePath: 'room',
      textAlign: 'left' as const,
      tdComponent: HotelPricingAssistantTableCellsTdRoom,
      variant: 'column' as const,
    };

    const dayColumns = getWeekDayColumns().map((column) => ({
      ...column,
      name: this.intl.t(`days_short.${column.isoWeekDay}`),
      valuePath: column.id.toString(),
      textAlign: 'left' as const,
      isEditing: this.isEditMode,
      onRateChange: this.onRateChange.bind(this),
      tdClassNames: 'td-strategy-table',
      tdComponent: TdRollingReferenceRate,
    }));

    return [roomColumn, ...dayColumns];
  }

  get isSaveButtonDisabled(): boolean {
    if (!this.strategy) {
      return true;
    }

    return !this.strategy.hasDirtyAttributes;
  }

  get rollingReferenceRatesSettings(): RollingReferenceRatesSettings | null {
    const { settings } = this.strategy ?? {};
    return settings?.settings_type === 'rolling_reference_rates'
      ? settings
      : null;
  }

  get rows(): Record<string, unknown>[] {
    const settings = this.rollingReferenceRatesSettings;
    if (!settings) {
      return [];
    }

    const hotel = this.hotelAndMarketService.hotel;
    const { pms_leading_room_id } = hotel.caxxxPmsSettings;
    const roomNameById = hotel.pricingIntegration.pms_id_to_pms_room_name;
    const { day_of_week } = settings;

    return (this.args.model.roomOffsetHierarchy ?? []).map((roomId) => {
      const roomName = roomNameById[roomId] ?? roomId;
      const row: Record<string, unknown> = {
        room: { roomName, isBaseRoom: pms_leading_room_id === roomName },
        roomId,
      };

      for (const [dayOfWeek, roomRates] of Object.entries(day_of_week)) {
        const rawValue = roomRates[roomId] ?? null;
        row[dayOfWeek] = {
          rawValue,
          formattedValue:
            rawValue !== null
              ? this.hotelAndMarketService.formatRate(rawValue)
              : '—',
          roomId,
        };
      }

      return row;
    });
  }

  get strategy(): RecommendationStrategy {
    return this.recommendationsController.strategiesPerType
      .rollingReferenceRatesStrategy;
  }

  // Edge case: If user makes a change and then navigates away without saving/canceling and then comes back
  // Rollback the unsaved changes on destroy
  willDestroy(): void {
    super.willDestroy();

    if (
      this.strategy &&
      !this.strategy.isDestroyed &&
      !this.strategy.isDestroying
    ) {
      this.strategy.rollbackAttributes();
    }
  }

  @action onCancelClick(): void {
    if (this.strategy) {
      this.strategy.rollbackAttributes();
    }

    this.isEditMode = false;
  }

  @action onEditClick(): void {
    this.isEditMode = true;
  }

  @action onRateChange(
    dayOfWeek: number,
    roomId: string,
    value: number | null,
  ): void {
    const settings = this.rollingReferenceRatesSettings;

    if (!this.strategy || !settings) {
      return;
    }

    this.strategy.settings = {
      ...settings,
      day_of_week: {
        ...settings.day_of_week,
        [dayOfWeek]: {
          ...settings.day_of_week[dayOfWeek],
          [roomId]: value,
        },
      },
    };
  }

  @action
  async onSaveClick(): Promise<void> {
    if (this.strategy) {
      await this.args.model.saveStrategyTask.perform(this.strategy);
    }

    this.isEditMode = false;
  }

  @action onToggleMode(): void {
    this.isAutoMode = !this.isAutoMode;
    this.isEditMode = false;
  }

  <template>
    <SettingsPageTemplate
      @description={{t
        "pricing_assistant.recommendations.settings.rolling_reference_rates.description_long"
      }}
      @title={{t
        "pricing_assistant.recommendations.settings.rolling_reference_rates.title"
      }}
      @type="container"
    >
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{t "pricing_assistant.recommendations.settings.main_title"}}
            @route="hotel.settings.recommendations.index"
          />
          <B.item
            @isCurrent={{true}}
            @label={{t
              "pricing_assistant.recommendations.settings.rolling_reference_rates.title"
            }}
          />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:link>
        <a
          class="prism-link"
          href="https://help.mylighthouse.com/en/articles/7857322-how-do-i-customize-my-recommendations"
          rel="noopener noreferrer"
          target="_blank"
        >{{t "general.learn_more"}}</a>
      </:link>
      <:default>
        <PrismCard @contentLayout="borderedHeader">
          <:content>
            <div class="flex flex-row items-start justify-between">
              <div class="flex flex-col gap-100">
                <h4>
                  {{if
                    this.isAutoMode
                    (t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.automatic_label"
                    )
                    (t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.manual_label"
                    )
                  }}
                </h4>
                <p class="paragraph-300--regular">
                  {{if
                    this.isAutoMode
                    (t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.automatic_description"
                    )
                    (t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.manual_description"
                    )
                  }}
                </p>
              </div>
              <PrismToggle
                @isChecked={{this.isAutoMode}}
                @onChange={{this.onToggleMode}}
              />
            </div>

            {{#unless this.isAutoMode}}
              <div class="flex flex-col gap-200 pt-300">
                <div class="flex flex-row items-center justify-between">
                  <h4>{{t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.table_title"
                    }}</h4>
                  {{#if this.isEditMode}}
                    <div class="flex flex-row gap-200">
                      <PrismButton
                        @buttonType="secondary"
                        @label={{t "general.cancel"}}
                        @onClick={{this.onCancelClick}}
                      />
                      <PrismButton
                        @isDisabled={{this.isSaveButtonDisabled}}
                        @label={{t "general.save"}}
                        @onClick={{this.onSaveClick}}
                      />
                    </div>
                  {{else}}
                    <PrismButton
                      @buttonType="secondary"
                      @iconBefore="edit-pencil"
                      @label={{t "general.edit"}}
                      @onClick={{this.onEditClick}}
                    />
                  {{/if}}
                </div>
                <PrismTable
                  @columns={{this.columns}}
                  @rows={{this.rows}}
                  @widthConstraint="eq-container"
                />
              </div>
            {{/unless}}
          </:content>
        </PrismCard>
      </:default>
    </SettingsPageTemplate>
  </template>
}
